import { useRef, useState } from "react";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Upload, Loader2 } from "lucide-react";

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function ExcelCard() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const exportXlsx = async () => {
    try {
      const res = await apiClient.get("/me/missions/export.xlsx", { responseType: "blob" });
      downloadBlob(res.data, "batisur-chantiers.xlsx");
      toast.success("Export Excel téléchargé");
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await apiClient.get("/me/missions/import-template.xlsx", { responseType: "blob" });
      downloadBlob(res.data, "batisur-modele-import.xlsx");
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const importXlsx = async (f) => {
    if (!f) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await apiClient.post("/me/missions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} chantier(s) créé(s) — rechargez la page pour les voir`);
        setTimeout(() => window.location.reload(), 2500);
      } else {
        toast.error("Aucun chantier créé — vérifiez les erreurs ci-dessous");
      }
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="excel-card">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-[#FF4D00]" />
          <div className="display text-2xl">Import / Export Excel</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadTemplate} className="btn-anthracite text-xs flex items-center gap-1.5" data-testid="excel-template-btn">
            <Download className="w-3.5 h-3.5" /> Modèle d'import
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-batisur text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid="excel-import-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Importer mes chantiers
          </button>
          <button onClick={exportXlsx} className="btn-anthracite text-xs flex items-center gap-1.5" data-testid="excel-export-btn">
            <Download className="w-3.5 h-3.5" /> Exporter mes chantiers
          </button>
        </div>
      </div>
      <div className="px-6 py-3 text-xs text-zinc-600">
        Créez jusqu'à 100 chantiers d'un coup depuis votre fichier Excel (titre, ville, budgets, échéance, honoraires, client final) — et exportez le suivi complet (montants, honoraires, statuts, échéances, artisans).
      </div>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => importXlsx(e.target.files?.[0])} data-testid="excel-file-input" />
      {result && (
        <div className="px-6 pb-4 text-sm" data-testid="excel-import-result">
          <span className="font-semibold text-emerald-700">{result.created} chantier(s) créé(s).</span>
          {result.errors?.length > 0 && (
            <ul className="mt-2 space-y-1 text-red-700 text-xs">
              {result.errors.map((er, i) => (
                <li key={i}>Ligne {er.row} : {er.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
